import { useEffect } from 'react';
import { useApiQuery } from '../query';
import { ObjectMap, useFeatureFlagContext } from './useFeatureFlagConfig';
import { useIsTokenExpired } from '../auth/user-info.hooks';

export function useFeatureFlag(
  params: string | string[],
  isEnabled = true
): any {
  const isMultiFlagRequest = typeof params === 'object';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const featureFlagValues: { [key: string]: string | undefined } = {};
  const url = `/shared/v1/feature-flags${
    isMultiFlagRequest ? `?keys=${params}` : `/${params}`
  }`;
  const { featureFlagCache, updateFeatureFlagCache } = useFeatureFlagContext();

  const doesFeatureFlagExist = (key?: string | string[]) => {
    let isFlagInCache = false;
    if (typeof key === 'string') {
      isFlagInCache = Object.keys(featureFlagCache).includes(key);
    } else if (Array.isArray(key)) {
      isFlagInCache = key.every((x) =>
        Object.keys(featureFlagCache).includes(x)
      );
    }
    return isFlagInCache;
  };
  const isTokenExpired = useIsTokenExpired();
  const query = useApiQuery<ObjectMap>(url, undefined, {
    enabled: isEnabled && !doesFeatureFlagExist(params) && !isTokenExpired,
  });

  const isReady =
    doesFeatureFlagExist(params) ||
    query?.status === 'error' ||
    query?.status === 'success';

  if (doesFeatureFlagExist(params) && isReady) {
    if (Array.isArray(params)) {
      for (const key in params) {
        featureFlagValues[params[key]] = featureFlagCache[params[key]];
      }
    } else {
      featureFlagValues[params] = featureFlagCache[params];
    }
  } else if (query?.data && isReady) {
    for (const key in query.data) {
      featureFlagValues[key] = query.data[key];
    }
    setDefaultForNonExistingKey();
  }

  useEffect(() => {
    if (isReady) {
      const newKeys = Object.keys(featureFlagValues).filter(
        (x) => Object.keys(featureFlagCache).includes(x) === false
      );

      if (newKeys.length > 0) {
        const newFeatureFlags: ObjectMap = Object.fromEntries(
          Object.entries(featureFlagValues).filter(([key]) =>
            newKeys.includes(key)
          )
        );
        updateFeatureFlagCache(newFeatureFlags);
      }
    }
  }, [featureFlagCache, featureFlagValues, isReady, updateFeatureFlagCache]);

  return {
    isReady,
    refetch: query.refetch,
    value: isMultiFlagRequest ? featureFlagValues : featureFlagValues[params],
  };

  function setDefaultForNonExistingKey() {
    if (Array.isArray(params)) {
      for (const param in params) {
        if (!Object.keys(featureFlagValues).includes(params[param])) {
          featureFlagValues[params[param]] = undefined;
        }
      }
    } else if (
      !isMultiFlagRequest &&
      !Object.keys(featureFlagValues).includes(params)
    ) {
      featureFlagValues[params] = undefined;
    }
  }
}
